import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Typography } from '@mui/material';
import PendingChangeWrapper from '@sharedCR/common/PendingChangeWrapper';
import { MoreButtonCDS } from '@sharedCR/coreDesignSystem/Buttons';
import { SortIcon } from '@sharedCR/coreDesignSystem/icons';
import MenuPopoverCDS from '@sharedCR/coreDesignSystem/MenuPopoverCDS';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { PpeType } from 'constants/company';
import { CENTRAL_TIME_ZONE, COMMON_DATETIME_WITH_ZONE_FORMAT } from 'constants/date';
import { PendingChangesActionType } from 'constants/pendingChanges';
import useLoanId from 'hooks/useLoanId';
import usePopover from 'hooks/usePopover';
import { formatDate } from 'utils/date';

import { openDialogAction } from 'store/global/actions';
import {
  selectLoanPricingEngineType,
  selectSortedRateSheets,
} from 'store/loanApplications/selectors';

import RateSheetForm from './RateSheetForm';

const RateSheetName = ({
  isFromPropertyAndLoan,
  setTempRateSheetId,
  rateSheetId,
  pendingChanges,
}) => {
  const dispatch = useDispatch();
  const loanId = useLoanId();

  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();

  const rateSheetList = useSelector(selectSortedRateSheets);
  const ppeType = useSelector(selectLoanPricingEngineType);

  const isLenderPpeFlow = ppeType === PpeType.LENDER_PPE;

  const { lifeTimeInfo: { createdDate, modifiedDate } = {} } =
    _.find(rateSheetList, i => i.rateSheetId === rateSheetId) || {};

  const formattedDate = modifiedDate || createdDate;

  const onOpenRateSheetPopup = useCallback(() => {
    dispatch(
      openDialogAction({
        header: 'Select Rate Sheet',
        body: () => (
          <RateSheetForm
            loanId={loanId}
            rateSheetId={rateSheetId}
            setTempRateSheetId={setTempRateSheetId}
          />
        ),
      }),
    );
  }, [dispatch, loanId, setTempRateSheetId, rateSheetId]);

  const items = useMemo(
    () => [
      {
        text: 'Change Rate Sheet',
        onClick: onOpenRateSheetPopup,
        icon: <SortIcon />,
      },
    ],
    [onOpenRateSheetPopup],
  );

  if (!formattedDate) {
    return null;
  }

  return (
    <span className="inline-flex items-center">
      <PendingChangeWrapper
        isSmall
        pendingChangesActionType={
          pendingChanges?.rateSheetId ? PendingChangesActionType.MODIFIED : undefined
        }
        textSelector="span"
      >
        <Typography color="text.secondary" variant="regular16">
          <span className="px-12">{`Rate Sheet: ${formatDate(
            formattedDate,
            COMMON_DATETIME_WITH_ZONE_FORMAT,
            { timeZone: CENTRAL_TIME_ZONE },
          )}`}</span>
        </Typography>
      </PendingChangeWrapper>

      {isFromPropertyAndLoan && isLenderPpeFlow && (
        <>
          <MoreButtonCDS isSelected={!!anchorEl} onClick={onSetAnchorElement} />

          <MenuPopoverCDS anchorEl={anchorEl} items={items} onClose={onClearAnchorElement} />
        </>
      )}
    </span>
  );
};

RateSheetName.propTypes = {
  setTempRateSheetId: PropTypes.func,
  isFromPropertyAndLoan: PropTypes.bool,
  rateSheetId: PropTypes.string,
  pendingChanges: PropTypes.object,
};

export default RateSheetName;
